import {
  Box,
  Card,
  Paper,
  Typography,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Button,
  Tooltip,
  Chip,
  IconButton,
} from '@mui/material';
import { KITCHEN_ORDERS_PAGE_SIZE } from './constants';
import { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../app/store/configureStore';
import {
  closeOrderStatusObserver,
  fetchKitchenOrdersAsync,
  observeOrderStatusChange,
  resetKitchenOrders,
  updateKitchenOrderItemUpdateAsync,
  updateKitchenOrderStatusAsync,
  StreamConnectionStatus,
  manualReconnectOrderStatusObserver,
  getStreamConnectionStatus,
  setConnectionStatus,
} from './kitchenOrdersSlice';
import KitchenOrderItem from './KitchenOrderItem';
import OrderItem from '../../../../app/data/models/orders/orderItem';
import KitchenSubOrder from '../../../../app/data/models/orders/kitchenSubOrder';
import OrderStatusLabel from '../../../../app/components/OrderStatusLabel';
import {
  orderStatusColor,
  orderStatusLabel,
} from '../../../../app/data/helpers/orderHelpers';
import Masonry from '@mui/lab/Masonry';
import EmptyScreen from '../../../../app/components/EmptyScreen';
import { format, differenceInMinutes } from 'date-fns';
import { OrderStatus } from '../../../../app/data/models/orders/orderStatus';
import { OrderType } from '../../../../app/data/models/orders/orderType';
import ListTitleBox from '../../../../app/components/list/ListTitleBox';
import { PullToRefresh } from '../../../../app/components/PullToRefresh';
import { LoadingButton } from '@mui/lab';
import RefreshIcon from '@mui/icons-material/Refresh';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import ReplayIcon from '@mui/icons-material/Replay';
import { toast } from 'react-toastify';

interface Props {
  kitchenOrders: KitchenSubOrder[];
  handleOrderItemClick: (
    orderItem: OrderItem,
    kitchenOrderId: string,
    orderId: string,
    kitchenOrders: KitchenSubOrder[]
  ) => void;
  handleStatusChange: (
    kitchenOrderId: string,
    orderId: string,
    newStatus: OrderStatus
  ) => void;
}

const statuses = [
  OrderStatus.Pending,
  OrderStatus.Processing,
  OrderStatus.Ready,
  OrderStatus.Cancelled,
];

const StatusChangeDialog = ({
  open,
  onClose,
  currentStatus,
  onStatusChange,
}: {
  open: boolean;
  onClose: () => void;
  currentStatus: OrderStatus;
  onStatusChange: (status: OrderStatus) => void;
}) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: 2,
          width: '100%',
          maxWidth: 360,
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: 'center',
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider',
          fontSize: '1.2rem',
          fontWeight: 600,
        }}
      >
        {t('kitchen.orders.dialog.change-status')}
      </DialogTitle>
      <DialogContent sx={{ p: 0, mt: 1 }}>
        <List sx={{ py: 0 }}>
          {statuses.map((status) => (
            <ListItem key={status} disablePadding>
              <ListItemButton
                onClick={() => {
                  onStatusChange(status);
                  onClose();
                }}
                selected={status === currentStatus}
                sx={{
                  py: 2,
                  px: 3,
                  transition: 'all 0.2s',
                  '&.Mui-selected': {
                    bgcolor: `${orderStatusColor(status)}15`,
                    '&:hover': {
                      bgcolor: `${orderStatusColor(status)}25`,
                    },
                  },
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <ListItemText
                  primary={t(orderStatusLabel(status))}
                  primaryTypographyProps={{
                    sx: {
                      color:
                        status === currentStatus
                          ? orderStatusColor(status)
                          : 'text.primary',
                      fontWeight: status === currentStatus ? 600 : 400,
                    },
                  }}
                />
                {status === currentStatus && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: orderStatusColor(status),
                      ml: 1,
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </DialogContent>
    </Dialog>
  );
};

const ConnectionStatusIndicator = ({
  status,
  onReconnect,
}: {
  status: StreamConnectionStatus;
  onReconnect: () => void;
}) => {
  const { t } = useTranslation();
  const [isReconnecting, setIsReconnecting] = useState(false);

  const handleReconnect = () => {
    setIsReconnecting(true);
    onReconnect();
    // Reset reconnecting state after a delay
    setTimeout(() => setIsReconnecting(false), 2000);
  };

  const getStatusDetails = () => {
    switch (status) {
      case StreamConnectionStatus.Connected:
        return {
          icon: <WifiIcon fontSize="small" />,
          label: t('kitchen.orders.connection.connected'),
          color: 'success',
          tooltip: t('kitchen.orders.connection.connected-tooltip'),
        };
      case StreamConnectionStatus.Disconnected:
        return {
          icon: <WifiOffIcon fontSize="small" />,
          label: t('kitchen.orders.connection.disconnected'),
          color: 'error',
          tooltip: t('kitchen.orders.connection.disconnected-tooltip'),
        };
      default:
        return {
          icon: <WifiOffIcon fontSize="small" />,
          label: t('kitchen.orders.connection.not-connected'),
          color: 'default',
          tooltip: t('kitchen.orders.connection.not-connected-tooltip'),
        };
    }
  };

  const statusDetails = getStatusDetails();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Tooltip title={statusDetails.tooltip}>
        <Chip
          icon={statusDetails.icon}
          label={statusDetails.label}
          size="small"
          color={statusDetails.color as 'success' | 'error' | 'default'}
        />
      </Tooltip>

      {(status === StreamConnectionStatus.Disconnected ||
        status === StreamConnectionStatus.NotConnected) && (
        <Tooltip title={t('kitchen.orders.connection.reconnect')}>
          <IconButton
            size="small"
            color="primary"
            onClick={handleReconnect}
            disabled={isReconnecting}
            sx={{ ml: 0.5 }}
          >
            {isReconnecting ? (
              <CircularProgress size={20} />
            ) : (
              <ReplayIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

const KitchenOrderComponent = ({
  kitchenOrders,
  handleOrderItemClick,
  handleStatusChange,
}: Props) => {
  const { t } = useTranslation();
  const [, setTick] = useState(0);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const clickCount = useRef(0);
  const clickTimeout = useRef<NodeJS.Timeout | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<KitchenSubOrder | null>(
    null
  );

  const handleClick = (order: KitchenSubOrder) => {
    clickCount.current += 1;

    if (clickCount.current === 1) {
      clickTimeout.current = setTimeout(() => {
        if (clickCount.current === 1) {
          // Single click
          setSelectedOrder(order);
          setStatusDialogOpen(true);
        }
        clickCount.current = 0;
      }, 300);
    } else if (clickCount.current === 2) {
      // Double click
      if (clickTimeout.current) {
        clearTimeout(clickTimeout.current);
      }
      clickCount.current = 0;
      if (order.orderStatus === OrderStatus.Processing) {
        handleStatusChange(order.id, order.orderId, OrderStatus.Ready);
      }
    }
  };

  // Clear timeout on unmount
  useEffect(() => {
    const timer = setInterval(() => {
      setTick((prev) => prev + 1);
    }, 1000);

    return () => {
      clearInterval(timer);
      if (clickTimeout.current) {
        clearTimeout(clickTimeout.current);
      }
    };
  }, []);

  const getTimeDisplay = (orderDateString: string, isCompleted: boolean) => {
    try {
      const orderDate = new Date(orderDateString);
      if (isNaN(orderDate.getTime())) {
        console.error('Invalid date:', orderDateString);
        return '--:--';
      }

      const now = new Date();
      const diffInMs = now.getTime() - orderDate.getTime();
      const hours = Math.floor(diffInMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diffInMs % (1000 * 60)) / 1000);

      // If more than 24 hours, show date
      if (hours >= 24 || isCompleted) {
        return format(orderDate, 'dd/MM/yyyy');
      }

      // Format time display
      if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      }
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    } catch {
      console.error('Error parsing date:', orderDateString);
      return '--:--';
    }
  };

  if (!kitchenOrders || kitchenOrders.length === 0)
    return <EmptyScreen emptyText={t('orders.list.empty')} />;

  const getTimerColor = (orderDateString: string, isCompleted: boolean) => {
    if (isCompleted) return 'success.main';

    try {
      const orderDate = new Date(orderDateString);
      if (isNaN(orderDate.getTime())) return 'text.secondary';

      const diffInMinutes = differenceInMinutes(new Date(), orderDate);
      if (diffInMinutes >= 20) return 'error.main';
      if (diffInMinutes >= 10) return 'warning.main';
      return 'text.secondary';
    } catch {
      return 'text.secondary';
    }
  };

  return (
    <>
      <Box component={Paper} sx={{ p: 2 }}>
        <Box sx={{ width: '100%' }}>
          <Masonry columns={{ xs: 1, sm: 2, md: 3, lg: 4, xl: 5 }} spacing={2}>
            {kitchenOrders.map((kitchenOrder) => (
              <Card
                key={kitchenOrder.id}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  borderLeft: 6,
                  borderColor: orderStatusColor(kitchenOrder.orderStatus),
                  transition:
                    'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
              >
                <Box
                  onClick={() => handleClick(kitchenOrder)}
                  sx={{
                    px: 1,
                    py: 0.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                    userSelect: 'none',
                  }}
                >
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      {`#${kitchenOrder.tokenNumber}`}
                    </Typography>
                    <Typography variant="caption" color={'text.secondary'}>
                      {kitchenOrder.orderType === OrderType.TakeAway
                        ? `${t('orders.type.takeaway')} - ${kitchenOrder.customerName}`
                        : kitchenOrder.tableNumber
                          ? `${t('orders.type.dine-in')} - ${t('orders.table')} ${kitchenOrder.tableNumber}`
                          : t('orders.type.dine-in')}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      textAlign: 'right',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: 0.5,
                    }}
                  >
                    <OrderStatusLabel orderStatus={kitchenOrder.orderStatus} />
                    <Typography
                      variant="caption"
                      sx={{
                        color: getTimerColor(
                          kitchenOrder.orderDate,
                          kitchenOrder.orderStatus === OrderStatus.Ready
                        ),
                        fontFamily: 'monospace',
                      }}
                    >
                      {getTimeDisplay(
                        kitchenOrder.orderDate,
                        kitchenOrder.orderStatus === OrderStatus.Ready ||
                          kitchenOrder.orderStatus === OrderStatus.Cancelled
                      )}
                    </Typography>
                  </Box>
                </Box>
                <Divider />

                <CardContent
                  sx={{
                    flexGrow: 1,
                    p: 0,
                    '&:last-child': {
                      paddingBottom: 0,
                    },
                  }}
                >
                  <KitchenOrderItem
                    kitchenOrder={kitchenOrder}
                    onOrderItemClick={(orderItem) =>
                      handleOrderItemClick(
                        orderItem,
                        kitchenOrder.id,
                        kitchenOrder.orderId,
                        kitchenOrders
                      )
                    }
                  />
                </CardContent>
              </Card>
            ))}
          </Masonry>
        </Box>
      </Box>

      <StatusChangeDialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        currentStatus={selectedOrder?.orderStatus ?? OrderStatus.Pending}
        onStatusChange={(status) => {
          if (selectedOrder) {
            handleStatusChange(selectedOrder.id, selectedOrder.orderId, status);
          }
          setStatusDialogOpen(false);
        }}
      />
    </>
  );
};

const KitchenOrders = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const kitchenName = searchParams.get('kitchenName');
  const showBackButton = searchParams.get('showBackButton') === 'true';
  const { restaurantId: restaurantIdString } = useParams<{
    restaurantId: string;
  }>();
  const restaurantId = restaurantIdString || '';
  const { kitchenId: kitchenIdString } = useParams<{ kitchenId: string }>();
  const kitchenId = kitchenIdString || '';
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    pendingKitchenOrders,
    completedKitchenOrders,
    isPendingLoading,
    isCompletedLoading,
    isPendingRefreshing,
    isCompletedRefreshing,
    pendingNewestDate,
    completedNewestDate,
    pendingOldestDate,
    completedOldestDate,
    pendingError,
    completedError,
    hasCompletedInitialLoad,
    connectionStatus,
  } = useAppSelector((state) => state.kitchenOrders);
  const [filterStatus, setFilterStatus] = useState<'pending' | 'completed'>(
    'pending'
  );

  const handleOrderItemClick = async (
    orderItem: OrderItem,
    kitchenOrderId: string,
    orderId: string,
    kitchenOrders: KitchenSubOrder[]
  ) => {
    dispatch(
      updateKitchenOrderItemUpdateAsync({
        orderItemId: orderItem.id,
        kitchenOrderId,
        restaurantId,
        orderId,
        kitchenId,
        kitchenOrders,
      })
    );
  };

  const handleStatusChange = async (
    kitchenOrderId: string,
    orderId: string,
    newStatus: OrderStatus
  ) => {
    dispatch(
      updateKitchenOrderStatusAsync({
        restaurantId,
        orderId,
        kitchenId,
        orderStatusUpdate: {
          orderId,
          subOrderStatuses: [
            {
              subOrderId: kitchenOrderId,
              orderStatus: newStatus,
              orderItemStatuses: [],
            },
          ],
        },
      })
    );
  };

  const handleRefresh = async () => {
    const statuses =
      filterStatus === 'pending'
        ? [OrderStatus.Pending, OrderStatus.Processing]
        : [OrderStatus.Ready, OrderStatus.Cancelled];

    const nextPageDate =
      filterStatus === 'pending'
        ? pendingNewestDate || undefined
        : completedNewestDate || undefined;

    await dispatch(
      fetchKitchenOrdersAsync({
        restaurantId,
        kitchenId,
        nextPageDate,
        statuses,
        pageSize: KITCHEN_ORDERS_PAGE_SIZE,
      })
    );
  };

  const handleLoadMore = () => {
    const statuses =
      filterStatus === 'pending'
        ? [OrderStatus.Pending, OrderStatus.Processing]
        : [OrderStatus.Ready, OrderStatus.Cancelled];

    const previousPageDate =
      filterStatus === 'pending'
        ? pendingOldestDate || undefined
        : completedOldestDate || undefined;

    dispatch(
      fetchKitchenOrdersAsync({
        restaurantId,
        kitchenId,
        previousPageDate,
        statuses,
        pageSize: KITCHEN_ORDERS_PAGE_SIZE,
      })
    );
  };

  const ordersToDisplay = useMemo(() => {
    const orders =
      filterStatus === 'completed'
        ? completedKitchenOrders
        : pendingKitchenOrders;

    return [...orders].sort((a, b) => {
      if (filterStatus === 'pending') {
        const aIsProcessing = a.orderStatus === OrderStatus.Processing;
        const bIsProcessing = b.orderStatus === OrderStatus.Processing;

        // 'Processing' orders come before 'Pending' orders.
        if (aIsProcessing && !bIsProcessing) return -1;
        if (!aIsProcessing && bIsProcessing) return 1;

        // If both are 'Processing', sort by statusChangedAt ascending.
        if (aIsProcessing && bIsProcessing) {
          const aTime = a.statusChangedAt
            ? new Date(a.statusChangedAt).getTime()
            : new Date(a.orderDate).getTime();
          const bTime = b.statusChangedAt
            ? new Date(b.statusChangedAt).getTime()
            : new Date(b.orderDate).getTime();
          return aTime - bTime;
        }

        // If both are 'Pending', sort by orderDate ascending.
        return (
          new Date(a.orderDate).getTime() - new Date(b.orderDate).getTime()
        );
      } else {
        // For the completed tab (Ready, Cancelled), sort by time in descending order.
        const aTime = a.statusChangedAt
          ? new Date(a.statusChangedAt).getTime()
          : new Date(a.orderDate).getTime();
        const bTime = b.statusChangedAt
          ? new Date(b.statusChangedAt).getTime()
          : new Date(b.orderDate).getTime();
        return bTime - aTime;
      }
    });
  }, [completedKitchenOrders, pendingKitchenOrders, filterStatus]);

  const handleFilterChange = (newStatus: 'pending' | 'completed') => {
    if (newStatus !== null && newStatus !== filterStatus) {
      setFilterStatus(newStatus);

      // Load completed orders if switching to completed tab and not loaded from backend yet
      if (newStatus === 'completed' && !hasCompletedInitialLoad) {
        dispatch(
          fetchKitchenOrdersAsync({
            restaurantId,
            kitchenId,
            statuses: [OrderStatus.Ready, OrderStatus.Cancelled],
            pageSize: KITCHEN_ORDERS_PAGE_SIZE,
          })
        );
      }
    }
  };

  const handleRetry = () => {
    const statuses =
      filterStatus === 'pending'
        ? [OrderStatus.Pending, OrderStatus.Processing]
        : [OrderStatus.Ready, OrderStatus.Cancelled];

    dispatch(
      fetchKitchenOrdersAsync({
        restaurantId,
        kitchenId,
        statuses,
        pageSize: KITCHEN_ORDERS_PAGE_SIZE,
      })
    );
  };

  const handleReconnect = () => {
    dispatch(manualReconnectOrderStatusObserver()({ restaurantId, kitchenId }));
  };

  // Reset orders and load new ones when kitchenId changes
  useEffect(() => {
    // Reset the orders in the store when kitchenId changes
    dispatch(resetKitchenOrders());

    // Load pending orders for the new kitchen
    dispatch(
      fetchKitchenOrdersAsync({
        restaurantId,
        kitchenId,
        statuses: [OrderStatus.Pending, OrderStatus.Processing],
        pageSize: KITCHEN_ORDERS_PAGE_SIZE,
      })
    );
  }, [dispatch, restaurantId, kitchenId]);

  useEffect(() => {
    dispatch(observeOrderStatusChange()({ restaurantId, kitchenId }));

    // Check connection status shortly after establishing the connection
    setTimeout(() => {
      const isConnected = getStreamConnectionStatus();
      dispatch(
        setConnectionStatus(
          isConnected
            ? StreamConnectionStatus.Connected
            : StreamConnectionStatus.Disconnected
        )
      );
    }, 500);

    return () => {
      dispatch(closeOrderStatusObserver());
    };
  }, [dispatch, restaurantId, kitchenId]);

  // Show a toast notification when connection status changes
  useEffect(() => {
    if (connectionStatus === StreamConnectionStatus.Connected) {
      toast.success(t('kitchen.orders.connection.reconnected'), {
        position: 'bottom-center',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } else if (connectionStatus === StreamConnectionStatus.Disconnected) {
      toast.warn(t('kitchen.orders.connection.lost'), {
        position: 'bottom-center',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  }, [connectionStatus, t]);

  const renderContent = () => {
    if (filterStatus === 'pending') {
      // Show full screen error only if no content
      if (isPendingLoading && !pendingKitchenOrders.length) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        );
      }

      if (pendingError && !pendingKitchenOrders.length) {
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              p: 4,
            }}
          >
            <Typography color="error">
              {t('kitchen.orders.error.load-failed')}
            </Typography>
            <Button
              variant="contained"
              onClick={handleRetry}
              startIcon={<RefreshIcon />}
            >
              {t('kitchen.orders.actions.try-again')}
            </Button>
          </Box>
        );
      }
    } else {
      if (
        !completedKitchenOrders.length &&
        (isCompletedLoading || !hasCompletedInitialLoad)
      ) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        );
      }

      if (completedError && !completedKitchenOrders.length) {
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              p: 4,
            }}
          >
            <Typography color="error">
              {t('kitchen.orders.error.load-failed')}
            </Typography>
            <Button
              variant="contained"
              onClick={handleRetry}
              startIcon={<RefreshIcon />}
            >
              {t('kitchen.orders.actions.try-again')}
            </Button>
          </Box>
        );
      }
    }

    return (
      <>
        <KitchenOrderComponent
          kitchenOrders={ordersToDisplay}
          handleOrderItemClick={handleOrderItemClick}
          handleStatusChange={handleStatusChange}
        />

        {/* Load More button */}
        {((filterStatus === 'pending' && pendingOldestDate) ||
          (filterStatus === 'completed' && completedOldestDate)) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <LoadingButton
              onClick={handleLoadMore}
              loading={
                filterStatus === 'pending'
                  ? isPendingLoading
                  : isCompletedLoading
              }
              variant="outlined"
            >
              {t('kitchen.orders.actions.load-more')}
            </LoadingButton>
          </Box>
        )}
      </>
    );
  };

  // Update error effect with specific messages for different scenarios
  useEffect(() => {
    if (pendingError && pendingKitchenOrders.length > 0) {
      // Check if it's a load more operation by checking if we already have orders
      const errorMessage = pendingOldestDate
        ? t('kitchen.orders.error.load-more-failed')
        : t('kitchen.orders.error.refresh-failed');

      toast.error(errorMessage, {
        position: 'bottom-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } else if (completedError && completedKitchenOrders.length > 0) {
      const errorMessage = completedOldestDate
        ? t('kitchen.orders.error.load-more-failed')
        : t('kitchen.orders.error.refresh-failed');

      toast.error(errorMessage, {
        position: 'bottom-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  }, [
    pendingError,
    completedError,
    pendingKitchenOrders.length,
    completedKitchenOrders.length,
    pendingOldestDate,
    completedOldestDate,
    t,
  ]);

  const titleElement = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'flex-start',
        width: '100%',
        gap: { xs: 0, sm: 1 },
      }}
    >
      <Typography variant="h4">
        {kitchenName || t('kitchen.orders.title')}
      </Typography>
      <ConnectionStatusIndicator
        status={connectionStatus}
        onReconnect={handleReconnect}
      />
    </Box>
  );

  return (
    <Box sx={{ width: '100%', p: 0 }}>
      <ListTitleBox
        title={titleElement}
        onBackClick={showBackButton ? () => navigate(-1) : undefined}
        primaryButtonText={t('kitchen.orders.actions.refresh')}
        primaryButtonProps={{
          loading:
            filterStatus === 'pending'
              ? isPendingRefreshing
              : isCompletedRefreshing,
        }}
        onPrimaryClick={handleRefresh}
      />

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <ToggleButtonGroup
          value={filterStatus}
          exclusive
          onChange={(_, newStatus) => handleFilterChange(newStatus)}
          size="small"
        >
          <ToggleButton value="pending">
            {t('kitchen.orders.tabs.pending')}
          </ToggleButton>
          <ToggleButton value="completed">
            {t('kitchen.orders.tabs.completed')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <PullToRefresh
        onRefresh={handleRefresh}
        isRefreshing={
          filterStatus === 'pending'
            ? isPendingRefreshing
            : isCompletedRefreshing
        }
      >
        {renderContent()}
      </PullToRefresh>
    </Box>
  );
};

export default KitchenOrders;
